import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useSnackbar } from "../contexts/SnackbarContext";
import { backendService } from "../services/backend.service";
import type { TeamStatsResponse } from "../utils/models";
import { TEAM_QUERY_STALE_TIME_MS, TEAM_STATS_QUERY_KEY } from "./useInvalidateTeamQueries";

/**
 * Which nurses the Team page's schedule-based stat cards count. Asked of the server, which reads the whole
 * month — the weeks the page has loaded may not reach that far back.
 */
export const useTeamStats = (departmentId: string) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { trackError } = useAnalytics();

	const query = useQuery<TeamStatsResponse>({
		queryKey: [...TEAM_STATS_QUERY_KEY, departmentId],
		queryFn: async () => {
			const response = await backendService.getTeamStats(departmentId);
			return response.data;
		},
		staleTime: TEAM_QUERY_STALE_TIME_MS,
	});

	useEffect(() => {
		if (query.isError) {
			trackError("team_stats_fetch_error", { department_id: departmentId });
			showSnackbar(t("team.stat.load_failed"), "error");
		}
	}, [query.isError, departmentId, trackError, showSnackbar, t]);

	return { stats: query.data, isLoading: query.isLoading, isError: query.isError };
};
